"use client";
import { RetweetOutlined } from "@ant-design/icons"; // Ant Design icon
import { Button, message, Modal, notification } from "antd"; // Ant Design components
import axios from "axios";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { cuttingOrder } from "../../../Data/cutting";
import { GET2, PATCH, POST } from "../../../services";
import {
  HDP_GET_CUTTING_EXECUTE,
  HDP_GET_CUTTING_STATUS,
  UPLIFT_EXECUTE_CUTTING,
  UPLIFT_LOGS_CUTTING,
} from "../../../services/path";

interface CuttingStatusResponse {
  success: boolean;
  message: string;
  result: {
    deviceNo: string;
    deviceConnected: string;
    isConnected: boolean;
  };
}

interface CuttingExecuteResponse {
  success: boolean;
  message: string;
  result: {
    partNo: string;
    message: string;
    balance: number;
    use: number;
  };
}

interface CuttingExecuteRequest {
  partNo: string;
}

// Mock cutting device (HDP) to always succeed — set REACT_APP_MOCK_CUTTING=true in env
// WARNING: test env only (GitLab var ENV_UC) — never set in ENV_UC_PROD
const IS_MOCK_CUTTING = process.env.REACT_APP_MOCK_CUTTING === "true";

const mockCheckStatus = (): Promise<CuttingStatusResponse> =>
  Promise.resolve({
    success: true,
    message: "MOCK",
    result: { deviceNo: "MOCK-DEVICE", deviceConnected: "MOCK", isConnected: true },
  });

const mockExecute = async (payload: CuttingExecuteRequest) => {
  await new Promise((resolve) => setTimeout(resolve, 800));
  const data: CuttingExecuteResponse = {
    success: true,
    message: "MOCK cutting success",
    result: { partNo: payload.partNo, message: "MOCK cutting success", balance: 999, use: 1 },
  };
  return { status: 200, data };
};

interface MyModalProps {
  open: boolean;
  onCancel: () => void;
  width?: string | number;
  centered?: boolean;
  bodyStyle?: React.CSSProperties;
  children?: React.ReactNode;
  user?: any; // Optional prop for user context
  selectedItem: cuttingOrder | null; // Optional prop for selected item
  batch_id: string;
}

const CuttingModal: React.FC<MyModalProps> = ({
  open,
  onCancel,
  width = "40vw",
  centered = true,
  bodyStyle = {
    padding: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "35vh",
  },
  user,
  selectedItem,
  batch_id,
}) => {
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [isCuttingProcessActive, setIsCuttingProcessActive] = useState(false);
  const throwError = (message: string, data: any) => {
    const error = new Error(message);
    error.cause = data;
    throw error;
  };

  // Close modal when attempts run out
  useEffect(() => {
    if (attemptsLeft <= 0) {
      message.error("ไม่สามารถลองใหม่ได้แล้ว");
      onCancel();
    }
  }, [attemptsLeft, onCancel]);

  // Reset attempts and cutting status when modal closes
  useEffect(() => {
    if (!open) {
      setAttemptsLeft(3);
      setIsCuttingProcessActive(false);
    }
  }, [open]);

  const handleCutting = useCallback(async () => {
    if (!selectedItem?.cutting_id) {
      message.error("ไม่พบข้อมูลการตัด กรุณาลองใหม่อีกครั้ง");
      setIsCuttingProcessActive(false);
      return null;
    }

    setIsCuttingProcessActive(true);
    message.loading({
      content: "กำลังตัด กรุณารอสักครู่...",
      duration: 0,
      key: "inProgress",
    });

    try {
      // 1. Check device status
      const checkStatus: CuttingStatusResponse = IS_MOCK_CUTTING
        ? await mockCheckStatus()
        : await GET2(HDP_GET_CUTTING_STATUS());
      if (!checkStatus?.success || !checkStatus?.result?.isConnected) {
        const errorMessage =
          checkStatus?.message ?? "ไม่สามารถตัดได้ กรุณาตรวจสอบการเชื่อมต่อกับเครื่องตัด";

        throwError(errorMessage, checkStatus);
      }

      // 4. Execute cutting
      const executePayload: CuttingExecuteRequest = {
        partNo: selectedItem.cutting_id.trim(),
      };

      const cuttingExecute = IS_MOCK_CUTTING
        ? await mockExecute(executePayload)
        : await axios.post(HDP_GET_CUTTING_EXECUTE(), executePayload);
      const executeData = cuttingExecute.data as CuttingExecuteResponse;
      if (!executeData.success) {
        const errorMessage =
          executeData.message ??
          "ไม่สามารถตัดได้ เนื่องจากเกิดข้อผิดพลาดในการตัด กรุณาลองใหม่อีกครั้ง";

        throwError(errorMessage, executeData);
      } else {
        message.success({
          content: `ตัดสำเร็จ: ${executeData.result.partNo} - ${executeData.result.message}`,
          duration: 2,
        });
      }

      console.log({
        service: "UPLIFT CHECKER FRONT",
        event: "cutting",
        httpStatusCode: cuttingExecute.status,
        requestPayload: {
          partNo: selectedItem.cutting_id.trim(),
        },
        responsePayload: cuttingExecute,
        createdBy: user?.user_id || "no user id",
      });

      // 5. Update cache quantity
      const cachePayload = {
        batch_id: batch_id,
        sku: selectedItem.sku,
        qty: selectedItem.current_qty ?? 0,
      };

      await PATCH(UPLIFT_EXECUTE_CUTTING, cachePayload);
      const logPayload = {
        sku: selectedItem.sku,
        barcode: selectedItem.barcode,
        item_name: selectedItem.item_name,
        cutting_id: selectedItem.cutting_id,
        is_success: true,
        response_json: JSON.stringify(executeData),
      };

      if (!IS_MOCK_CUTTING) await POST(UPLIFT_LOGS_CUTTING, logPayload);
      onCancel(); // Close the modal after successful cutting
      return executeData.message;
    } catch (error: any) {
      console.error("Error during cutting process:", error || "");
      message.destroy("inProgress"); // Hide loading message
      notification.error({
        message: "เกิดข้อผิดพลาด",
        description: error ?? "เกิดข้อผิดพลาดในการตัด กรุณาลองใหม่อีกครั้ง",
      });

      console.log({
        service: "UPLIFT CHECKER FRONT",
        event: "cutting",
        httpStatusCode: error?.message === "INTERNAL_SERVER_ERROR" ? 500 : 400,
        requestPayload: {
          partNo: selectedItem.cutting_id.trim(),
        },
        responsePayload: error,
        createdBy: user?.user_id || "no user id",
      });

      const logPayload = {
        sku: selectedItem.sku,
        barcode: selectedItem.barcode,
        item_name: selectedItem.item_name,
        cutting_id: selectedItem.cutting_id,
        is_success: false,
        error_message: JSON.stringify(error) ?? "",
      };

      if (!IS_MOCK_CUTTING) await POST(UPLIFT_LOGS_CUTTING, logPayload);
      setAttemptsLeft((prev) => prev - 1);
      return null;
    } finally {
      message.destroy("inProgress");
      setIsCuttingProcessActive(false); //set cutting status to false after process completes
    }
  }, [selectedItem, onCancel]);

  // Trigger cutting process when modal opens
  useEffect(() => {
    if (open) {
      handleCutting();
    }
  }, [open, handleCutting]);

  const handleRetry = () => {
    if (attemptsLeft > 0) {
      handleCutting();
    } else {
      message.error("ไม่สามารถลองใหม่ได้แล้ว");
      onCancel();
    }
  };

  return (
    <div className="flex justify-center items-center">
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={width}
        centered={centered}
        bodyStyle={bodyStyle}
      >
        {isCuttingProcessActive ? (
          <h1 className="font-thin text-blue-500">กำลังตัด...</h1>
        ) : (
          <Button
            type="primary"
            icon={<RetweetOutlined />}
            onClick={handleRetry}
            disabled={attemptsLeft <= 0}
          >
            ตัดอีกครั้ง ( ลองได้อีก {attemptsLeft} ครั้ง)
          </Button>
        )}
      </Modal>
    </div>
  );
};

export default CuttingModal;
